
<!--
  @name: CustomScrollbar
  @description: 自定义滚动条组件
-->
<template>
  <el-scrollbar
    ref="scrollbarRef"
    wrap-class="horizontal-wrap"
    view-class="horizontal-view"
    :style="{ height: `${height}px` }"
  >
    <slot />
  </el-scrollbar>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';

defineProps({
  height: {
    type: Number,
    default: 88,
  },
});

const scrollbarRef = ref();
let isDragging = ref(false);
let startX = ref(0);
let scrollStart = ref(0);

/**
 * 鼠标滚轮滚动
 */
function onWheel(e: WheelEvent) {
  const wrap = scrollbarRef.value?.wrapRef;

  if (!wrap) return;

  // 强制将垂直滚轮（deltaY）转为横向滚动
  e.preventDefault();
  wrap.scrollLeft += e.deltaY;
}

/**
 * 鼠标按下
 */
function onMouseDown(e: MouseEvent) {
  const wrap = scrollbarRef.value?.wrapRef;

  if (!wrap) return;

  isDragging.value = true;
  startX.value = e.pageX;
  scrollStart.value = wrap.scrollLeft;
  wrap.style.cursor = 'grabbing';
}

/**
 * 鼠标移动
 */
function onMouseMove(e: MouseEvent) {
  if (!isDragging.value) return;
  const wrap = scrollbarRef.value?.wrapRef;

  if (!wrap) return;

  const distance = e.pageX - startX.value;

  wrap.scrollLeft = scrollStart.value - distance;
}

/**
 * 鼠标抬起
 */
function onMouseUp() {
  isDragging.value = false;
  const wrap = scrollbarRef.value?.wrapRef;

  if (wrap) wrap.style.cursor = 'grab';
}

/**
 * 挂载
 */
onMounted(() => {
  const wrap = scrollbarRef.value?.wrapRef;

  if (!wrap) return;

  wrap.addEventListener('wheel', onWheel, { passive: false });
  wrap.addEventListener('mousedown', onMouseDown);
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
});

/**
 * 卸载
 */
onBeforeUnmount(() => {
  const wrap = scrollbarRef.value?.wrapRef;

  if (!wrap) return;

  wrap.removeEventListener('wheel', onWheel);
  wrap.removeEventListener('mousedown', onMouseDown);
  document.removeEventListener('mousemove', onMouseMove);
  document.removeEventListener('mouseup', onMouseUp);
});
</script>

<style scoped lang="scss">
:deep(.horizontal-wrap) {
  overflow-x: auto !important;
  overflow-y: hidden !important;
  white-space: nowrap;
  cursor: grab;
  user-select: none;
  scroll-behavior: smooth;
}

:deep(.horizontal-view) {
  display: inline-flex;
  gap: 12px;
}
</style>
